import { useMemo } from 'react';
import {
  FUNDING_TYPE_OPTIONS,
  NYC_SET_ASIDE_OPTION,
  SET_ASIDE_OPTIONS,
} from './constants';
import v1Messages from '../ManageBuildings/messages';
import appMessages from '../App/messages';
import messages from './messages';
import type { Gates } from './types';

const useHeaders = ({
  gates,
  intl,
  constructionTypeOptions,
}: {
  gates: Gates,
  intl: Object,
  constructionTypeOptions: { value: string, text: string }[],
}) =>
  useMemo(() => {
    const {
      isAffordableProperty,
      isPHProperty,
      isRDProperty,
      isLIHTCProperty,
      showFractions,
      showFundingType,
      showCertOccConstructionType,
    } = gates;
    const yesNoOptions = [
      {
        value: intl.formatMessage(appMessages.yes),
        text: intl.formatMessage(appMessages.yes),
      },
      {
        value: intl.formatMessage(appMessages.no),
        text: intl.formatMessage(appMessages.no),
      },
    ];

    return [
      {
        id: 'name',
        label: intl.formatMessage(v1Messages.buildingNumber),
        dataType: 'string',
        sortable: true,
        searchable: true,
      },
      {
        id: 'buildingAddress',
        label: intl.formatMessage(v1Messages.buildingAddress),
        dataType: 'string',
        sortable: true,
        searchable: true,
      },
      {
        id: 'unitsCount',
        label: intl.formatMessage(messages.unitsCount),
        dataType: 'number',
        sortable: true,
      },
      ...(isAffordableProperty
        ? [
            {
              id: 'XMLSiteName',
              label: intl.formatMessage(v1Messages.xmlAltSiteName),
              dataType: 'string',
              sortable: true,
              searchable: true,
            },
          ]
        : []),
      {
        id: 'placedInServiceDate',
        label: intl.formatMessage(v1Messages.buildingPISD),
        dataType: 'date',
        sortable: true,
      },
      {
        id: 'buildingIDNumber',
        label: intl.formatMessage(v1Messages.buildingBIN),
        dataType: 'string',
        sortable: true,
        searchable: true,
      },
      ...(isAffordableProperty && isPHProperty
        ? [
            {
              id: 'PHBuildingNumber',
              label: intl.formatMessage(v1Messages.phBuildingNumber),
              dataType: 'string',
              sortable: true,
              searchable: true,
            },
            {
              id: 'PHBuildingEntrance',
              label: intl.formatMessage(v1Messages.phBuildingEntrance),
              dataType: 'string',
              sortable: true,
              searchable: true,
            },
          ]
        : []),
      ...(isAffordableProperty && isRDProperty
        ? [
            {
              id: 'RDSiteId',
              label: intl.formatMessage(v1Messages.rdSiteId),
              dataType: 'string',
              sortable: true,
              searchable: true,
            },
          ]
        : []),
      ...(isAffordableProperty && isLIHTCProperty
        ? [
            {
              id: 'minimumSetAside',
              label: intl.formatMessage(v1Messages.minimumSetAside),
              dataType: 'string',
              sortable: true,
              filterOptions: [...SET_ASIDE_OPTIONS, NYC_SET_ASIDE_OPTION],
            },
          ]
        : []),
      ...(showFractions
        ? [
            {
              id: 'applicableFractionUnit',
              label: intl.formatMessage(v1Messages.applicableFractionUnit),
              info: intl.formatMessage(
                v1Messages.applicableFractionUnitTooltip,
              ),
              dataType: 'number',
              sortable: true,
            },
            {
              id: 'applicableFractionFloorSpace',
              label: intl.formatMessage(
                v1Messages.applicableFractionFloorSpace,
              ),
              info: intl.formatMessage(
                v1Messages.applicableFractionFloorSpaceTooltip,
              ),
              dataType: 'number',
              sortable: true,
            },
          ]
        : []),
      ...(isAffordableProperty
        ? [
            {
              id: 'heraSpecial',
              label: intl.formatMessage(v1Messages.heraSpecial),
              dataType: 'string',
              sortable: true,
              filterOptions: yesNoOptions,
            },
          ]
        : []),
      ...(showFundingType
        ? [
            {
              id: 'fundingType',
              label: intl.formatMessage(v1Messages.fundingType),
              dataType: 'string',
              sortable: true,
              filterOptions: FUNDING_TYPE_OPTIONS.map(({ text }) => ({
                value: text,
                text,
              })),
            },
          ]
        : []),
      ...(showCertOccConstructionType
        ? [
            {
              id: 'certificateOfOccupancyDate',
              label: intl.formatMessage(v1Messages.certificateOfOccupancyDate),
              dataType: 'date',
              sortable: true,
            },
            {
              id: 'constructionType',
              label: intl.formatMessage(v1Messages.constructionType),
              dataType: 'string',
              sortable: true,
              filterOptions: constructionTypeOptions.map(({ text }) => ({
                value: text,
                text,
              })),
            },
          ]
        : []),
      {
        id: 'actions',
        label: intl.formatMessage(appMessages.actions),
      },
    ];
  }, [gates, intl, constructionTypeOptions]);

export default useHeaders;
